import { useQuery } from '@apollo/client/react'
import { dedupe } from '@/helpers/dedupe'
import { Table as BaseTable, type TableProps } from '../table'
import { usePageInfo } from '@/hooks/usePageInfo'
import { Slimline as Error } from '@/components/error'
import { LoadingSkeleton } from '@/components/base/loading-skeleton'
import { graphql } from '@/gql'
import type { ItemFilters, ItemOrder } from '@/gql/graphql'
import { useState } from 'react'
import { Create } from './create'

const ItemsQuery = graphql(`
  query Items($filters: ItemFilters, $order: ItemOrder, $after: String) {
    items(filters: $filters, order: $order, after: $after) {
      nodes {
        id
        name
      }
      count
      pageInfo { hasNextPage hasPreviousPage startCursor endCursor }
    }
  }
`)

interface Props extends TableProps {
  filters?: ItemFilters
}

export const Table = ({ filters = {}, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery(ItemsQuery, { variables: { filters } })
  const [showCreate, setShowCreate] = useState(false)

  const { pageInfo } = usePageInfo(data?.items?.pageInfo)

  if (loading) return <LoadingSkeleton table />

  return (
    <>
      { error && <Error error={ error } /> }
      { data && <BaseTable<ItemFilters, ItemOrder>
        { ...props }
        initialFilters={ filters }
        moreToFetch={ pageInfo.current?.hasNextPage }
        refetch={ refetch }
        totalRows={ data.items.count }
        actionButton={{
          name: 'New',
          color: 'purple',
          callback: () => setShowCreate(true)
        }}
        fetchMore={ () => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.items.nodes = dedupe([...previousData.items.nodes, ...fetchMoreResult.items.nodes])
            return { ...fetchMoreResult }
          }
        }) }
        headings={ [
          { name: 'Name' },
        ] }
        rows={ data?.items?.nodes.map(item => ({
          id: item.id,
          columns: [
            item.name,
          ],
          link: `/items/${item.id}`,
        })) }
      /> }
      <Create open={ showCreate } setOpen={ setShowCreate } />
    </>
  )
}
